import { Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { ProductionDeclarationMaterialAdditionalFilesCard } from '@/features/production-declaration/components/statement/production-declaration-material-additional-files-card';
import { ProductionDeclarationMaterialCommodityOriginDeleteDialog } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origin-delete-dialog';
import { ProductionDeclarationMaterialCommodityOriginsBanner } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origins-banner';
import { ProductionDeclarationMaterialCommodityOriginsLoadingState } from '@/features/production-declaration/components/statement/production-declaration-material-commodity-origins-loading-state';
import { ProductionDeclarationMaterialPlacesCard } from '@/features/production-declaration/components/statement/production-declaration-material-places-card';
import { ProductionDeclarationMaterialSupplyChainCard } from '@/features/production-declaration/components/statement/production-declaration-material-supply-chain-card';
import { useCanEditProductionDeclaration } from '@/features/production-declaration/hooks/use-can-edit-production-declaration';
import { useProductionDeclarationMaterialCommodityOriginCreate } from '@/features/production-declaration/hooks/use-production-declaration-material-commodity-origin-create';
import { useProductionDeclarationMaterialCommodityOriginDelete } from '@/features/production-declaration/hooks/use-production-declaration-material-commodity-origin-delete';
import type { ProductionDeclarationCommodityOrigin } from '@/types/core/purchase-order/production-declaration-commodity-origin';
import {
  ProductionDeclarationMaterialStatusCode,
  type ProductionDeclarationMaterialStatusCode as ProductionDeclarationMaterialStatusCodeType,
} from '@/types/core/purchase-order/production-declaration-material-status-code';
import type { ProductionDeclarationStatusCode as ProductionDeclarationStatusCodeType } from '@/types/core/purchase-order/production-declaration-status-code';

type ProductionDeclarationMaterialCommodityOriginsProps = {
  canComment?: boolean;
  commodityOrigins: ProductionDeclarationCommodityOrigin[];
  isLoading?: boolean;
  materialId: string;
  materialStatusCode?: ProductionDeclarationMaterialStatusCodeType;
  productionDeclarationId: string;
  statusCode: ProductionDeclarationStatusCodeType;
  token?: string;
};

export function ProductionDeclarationMaterialCommodityOrigins({
  canComment = false,
  commodityOrigins,
  isLoading = false,
  materialId,
  materialStatusCode,
  productionDeclarationId,
  statusCode,
  token,
}: ProductionDeclarationMaterialCommodityOriginsProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });
  const { handleCreate, isPending: isCreatingCommodityOrigin } =
    useProductionDeclarationMaterialCommodityOriginCreate({
      materialId,
      productionDeclarationId,
      token,
    });
  const {
    handleDelete,
    handleDeleteDialogOpenChange,
    isDeleteDialogOpen,
    isPending: isDeletingCommodityOrigin,
    openDeleteDialog,
  } = useProductionDeclarationMaterialCommodityOriginDelete({
    materialId,
    productionDeclarationId,
    token,
  });

  const canCreateCommodityOrigin = useCanEditProductionDeclaration(statusCode);

  if (isLoading) {
    return <ProductionDeclarationMaterialCommodityOriginsLoadingState />;
  }

  const hasMultipleOrigins = commodityOrigins.length > 1;
  const canDeleteCommodityOrigin =
    canCreateCommodityOrigin && hasMultipleOrigins;
  const showSupplierCorrectionComments =
    materialStatusCode === ProductionDeclarationMaterialStatusCode.Rejected;

  return (
    <section className='space-y-7'>
      <ProductionDeclarationMaterialCommodityOriginsBanner
        disabled={!canCreateCommodityOrigin}
        hasMultipleOrigins={hasMultipleOrigins}
        isCreating={isCreatingCommodityOrigin}
        onCreate={canCreateCommodityOrigin ? handleCreate : undefined}
      />
      {commodityOrigins.length === 0 ? (
        <div className='rounded-[1.4rem] border border-declaration-border border-dashed bg-white px-7 py-10 text-center text-declaration-muted-foreground shadow-[0_10px_24px_rgba(17,24,39,0.04)] sm:px-8'>
          {t('materialCommodityOrigins.empty')}
        </div>
      ) : (
        commodityOrigins.map((commodityOrigin) => (
          <div className='space-y-5' key={commodityOrigin.id}>
            <div className='flex items-center gap-4'>
              <h3 className='font-semibold text-[1.45rem] text-declaration-foreground tracking-tight'>
                {hasMultipleOrigins
                  ? t('materialCommodityOrigins.titleWithCounter', {
                      count: commodityOrigin.sequenceNumber,
                    })
                  : t('materialCommodityOrigins.title')}
              </h3>
              {canDeleteCommodityOrigin ? (
                <Button
                  aria-label={t('materialCommodityOrigins.actions.delete')}
                  className='h-auto p-0 text-declaration-foreground shadow-none transition-colors hover:bg-transparent hover:text-declaration-foreground-muted dark:text-declaration-foreground dark:hover:bg-transparent dark:hover:text-declaration-foreground-muted'
                  disabled={isDeletingCommodityOrigin}
                  onClick={() => {
                    openDeleteDialog(commodityOrigin);
                  }}
                  type='button'
                  variant='ghost'
                >
                  <Trash2 className='size-[1.35rem]' strokeWidth={2.1} />
                </Button>
              ) : null}
            </div>
            <ProductionDeclarationMaterialPlacesCard
              canComment={canComment}
              commodityOriginId={commodityOrigin.id}
              showSupplierCorrectionComments={showSupplierCorrectionComments}
              statusCode={statusCode}
            />
            <ProductionDeclarationMaterialSupplyChainCard
              canComment={canComment}
              commodityOriginId={commodityOrigin.id}
              showSupplierCorrectionComments={showSupplierCorrectionComments}
              statusCode={statusCode}
            />
            <ProductionDeclarationMaterialAdditionalFilesCard
              canComment={canComment}
              commodityOriginId={commodityOrigin.id}
              showSupplierCorrectionComments={showSupplierCorrectionComments}
              statusCode={statusCode}
            />
          </div>
        ))
      )}
      <ProductionDeclarationMaterialCommodityOriginDeleteDialog
        isPending={isDeletingCommodityOrigin}
        onConfirm={handleDelete}
        onOpenChange={handleDeleteDialogOpenChange}
        open={isDeleteDialogOpen}
      />
    </section>
  );
}
